import React from 'react'
import PropTypes from 'prop-types'
import { FileText, Download, Mail } from 'lucide-react'

import { Button } from '../../ui/button'
import { Card } from '../../ui/card'

function ReportPreview({ reportType, previewData, formData, onConfirm, onCancel, isGenerating }) {
  const reportTypeNames = {
    lts_registration: 'LTS Registration Report',
    lts_monthly: 'Monthly LTS Report',
    enrollment: 'Enrollment Report',
    klass_package_roster: 'Class Roster Report',
    skill_progress: 'Skill Progress Report',
    staff_coach: 'Staff & Coaches Report'
  }

  const deliveryIcons = {
    email: <Mail className="h-4 w-4" />,
    download: <Download className="h-4 w-4" />,
    both: (
      <>
        <Mail className="h-4 w-4" />
        <Download className="h-4 w-4" />
      </>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Report Preview</h2>
        <p className="text-gray-600">Review the report details before generating</p>
      </div>

      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <FileText className="h-5 w-5 text-purple-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-lg">{reportTypeNames[reportType]}</h3>
              <p className="text-sm text-gray-600 mt-1">{previewData.parameters_summary}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4 border-t">
            <div>
              <p className="text-sm text-gray-600">Estimated Rows</p>
              <p className="text-2xl font-semibold text-purple-600">{previewData.estimated_rows.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">File Format</p>
              <p className="text-2xl font-semibold">{formData.file_format.toUpperCase()}</p>
            </div>
          </div>

          <div className="pt-4 border-t">
            <p className="text-sm text-gray-600 mb-2">Delivery Method</p>
            <div className="flex items-center space-x-2 text-sm">
              {deliveryIcons[formData.delivery_method]}
              <span className="capitalize">
                {formData.delivery_method === 'both' ? 'Email & Download' : formData.delivery_method}
              </span>
            </div>
          </div>

          {previewData.estimated_rows === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
              <p className="text-sm text-yellow-800">
                <strong>Warning:</strong> This report will be empty based on your current filters.
              </p>
            </div>
          )}
        </div>
      </Card>

      <div className="flex space-x-3">
        <Button onClick={onCancel} variant="outline" className="flex-1" disabled={isGenerating}>
          Back to Edit
        </Button>
        <Button onClick={onConfirm} className="flex-1" disabled={isGenerating}>
          {isGenerating ? 'Generating Report...' : 'Generate Report'}
        </Button>
      </div>
    </div>
  )
}

ReportPreview.propTypes = {
  reportType: PropTypes.string.isRequired,
  previewData: PropTypes.shape({
    estimated_rows: PropTypes.number.isRequired,
    parameters_summary: PropTypes.string.isRequired
  }).isRequired,
  formData: PropTypes.object.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isGenerating: PropTypes.bool.isRequired
}

export default ReportPreview
